import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { AuthorizationParams } from '@auth0/auth0-react';
import { z } from 'zod';
import useUser from '../hooks/useUser';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, KeyRound, Pencil } from 'lucide-react';
import { Button, Input, Panel, cn } from '../design-system';
import { pushEvent, ANALYTICS_EVENTS, trackLeadSubmit } from '../utils/analytics';
import fetchWithTimeout from '../utils/fetchWithTimeout';
import { getAuth0RedirectOrigin } from '../utils/auth0Origin';
import { getApiBaseUrl } from '../utils/apiBaseUrl';
import {
  clearHighRiskStepUpActionId,
  rememberHighRiskStepUpActionId,
  rememberSecurityReauthIntent,
  clearSecurityReauthIntent,
} from '../utils/highRiskStepUpStorage';
import {
  buildPostSignupDecisionPath,
  completePostSignupJourney,
  hasPendingPostSignupJourney,
  rememberPendingPostSignupDestination,
  startPostSignupJourney,
} from '../utils/postSignupJourney';
import {
  buildGoogleSearchLeadAttribution,
  type GoogleSearchLeadAttribution,
} from '../utils/googleSearchLeadAttribution';
import {
  getWebsiteThemeAttribution,
  type WebsiteThemeAttribution,
} from '../experiments/websiteTheme';
import './Auth/AuthSurface.css';
import { getMyAccountAudience, MY_ACCOUNT_SCOPES } from '../services/myAccountMfa';
import type { SecurityAuthorization } from '../services/myAccountMfa';
import { loginWithPasskey, PasskeyLoginError } from '../services/passkeyLogin';
import { getBrowserTimeZone } from '../utils/browserTimeZone';
import { getUtmParamsForApi } from '../utils/utmTracker';

type AuthProvider = 'google' | 'apple' | 'passkey' | 'email';
type HostedProvider = Exclude<AuthProvider, 'email'>;
type EmailAuthIntent = 'signin' | 'signup';
type EmailAuthStep = 'email' | 'password';

type EmbeddedAuthRequest = {
  email: string;
  password: string;
  highRiskStepUpActionId?: string;
  security?: boolean;
  securityAccountToken?: string;
  leadAttribution?: Partial<GoogleSearchLeadAttribution & WebsiteThemeAttribution> &
    Record<string, string>;
  signupTimeZone?: string;
};

type EmbeddedAuthResponse = {
  success?: boolean;
  token?: string;
  mfaChallenge?: string;
  mfaMethod?: 'email' | 'sms';
  recoveryCode?: string;
  securityAuthorization?: SecurityAuthorization;
  code?: string;
  message?: string;
  requiresLogin?: boolean;
  verificationEmailSent?: boolean;
  isNewUser?: boolean;
  postSignupJourneyRequired?: boolean;
  user?: { id?: string | number };
};

type EmbeddedAccountStatusResponse = {
  success?: boolean;
  exists?: boolean;
  code?: string;
  message?: string;
};

type SignupPasswordStrengthTone = 'neutral' | 'danger' | 'success';

type SignupPasswordStrength = {
  message: string;
  tone: SignupPasswordStrengthTone;
  valid: boolean;
};

const AUTH_REQUEST_TIMEOUT_MS = 15000;
const PASSWORD_MANAGER_COMMIT_GRACE_MS = 120;
const PASSWORD_MANAGER_DELAYED_COMMIT_TIMEOUT_MS = 1500;
const PASSWORD_MANAGER_COMMIT_POLL_MS = 25;
const PASSWORD_MANAGER_PASSWORD_SETTLE_MS = 150;
const SIGNUP_PASSWORD_MIN_LENGTH = 8;
const DISABLED_EMAIL_AUTH_MESSAGE =
  'Email sign-in is temporarily unavailable. Try again shortly or continue with Google or Apple.';
const ACCOUNT_STATUS_UNAVAILABLE_MESSAGE = 'We could not connect to sign in. Please try again.';
const EMAIL_VERIFICATION_REQUIRED_MESSAGE =
  'Email is not verified. Send another verification email below.';
const VERIFICATION_EMAIL_SENT_MESSAGE =
  'Verification email sent. Check your inbox and spam, then continue with email.';
const SIGNUP_PASSWORD_REQUIREMENT_MESSAGE =
  'Password needs 8+ characters with letters and numbers.';
const PASSWORD_HAS_LETTER_PATTERN = /[A-Za-z]/;
const PASSWORD_HAS_NUMBER_PATTERN = /\d/;
const PASSWORD_HAS_SYMBOL_PATTERN = /[^A-Za-z0-9]/;
const PASSWORD_HAS_LOWER_PATTERN = /[a-z]/;
const PASSWORD_HAS_UPPER_PATTERN = /[A-Z]/;

const providerEventMethod: Record<AuthProvider, string> = {
  google: 'google',
  apple: 'apple',
  passkey: 'passkey',
  email: 'embedded_email',
};

const GoogleGlyph = (): React.JSX.Element => (
  <svg aria-hidden="true" className="auth-google-glyph" focusable="false" viewBox="0 0 18 18">
    <path
      d="M17.64 9.204c0-.638-.057-1.252-.164-1.84H9v3.482h4.844c-.209 1.125-.843 2.078-1.793 2.716v2.258h2.908c1.702-1.567 2.681-3.875 2.681-6.616z"
      fill="#4285F4"
    />
    <path
      d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.583-5.036-3.71H.957v2.332A8.997 8.997 0 0 0 9 18z"
      fill="#34A853"
    />
    <path
      d="M3.964 10.712A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.712V4.956H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.044l3.007-2.332z"
      fill="#FBBC05"
    />
    <path
      d="M9 3.579c1.322 0 2.508.454 3.44 1.346l2.582-2.582C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.956l3.007 2.332C4.672 5.161 6.656 3.579 9 3.579z"
      fill="#EA4335"
    />
  </svg>
);

const AppleGlyph = (): React.JSX.Element => (
  <svg aria-hidden="true" className="auth-apple-glyph" focusable="false" viewBox="0 0 24 24">
    <path
      d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.26-.07 2.14.69 2.88.75 1.11-.23 2.17-.89 3.35-.8 1.42.12 2.49.67 3.2 1.69-2.93 1.76-2.23 5.62.45 6.7-.54 1.43-1.23 2.86-1.88 4.63ZM12.03 7.25C11.88 5.13 13.61 3.38 15.59 3.2c.27 2.45-2.22 4.28-3.56 4.05Z"
      fill="currentColor"
    />
  </svg>
);

const socialProviders: Array<{
  provider: HostedProvider;
  label: string;
  icon: React.ReactNode;
  connection: string;
}> = [
  {
    provider: 'google',
    label: 'Continue with Google',
    icon: <GoogleGlyph />,
    connection: 'google-oauth2',
  },
  {
    provider: 'apple',
    label: 'Continue with Apple',
    icon: <AppleGlyph />,
    connection: 'apple',
  },
  {
    provider: 'passkey',
    label: 'Continue with a passkey',
    icon: <KeyRound size={19} />,
    connection: 'Username-Password-Authentication',
  },
];

const embeddedAuthEndpoint = (path: string): string => {
  const apiBase = getApiBaseUrl();
  return `${apiBase || ''}/auth/embedded/${path}`;
};

const evaluateSignupPasswordStrength = (candidate: string): SignupPasswordStrength => {
  if (!candidate) {
    return {
      message: 'Use 8+ characters with letters and numbers.',
      tone: 'neutral',
      valid: false,
    };
  }

  const longEnough = candidate.length >= SIGNUP_PASSWORD_MIN_LENGTH;
  const hasLetter = PASSWORD_HAS_LETTER_PATTERN.test(candidate);
  const hasNumber = PASSWORD_HAS_NUMBER_PATTERN.test(candidate);

  if (!longEnough || !hasLetter || !hasNumber) {
    return {
      message: SIGNUP_PASSWORD_REQUIREMENT_MESSAGE,
      tone: 'danger',
      valid: false,
    };
  }

  const strengthSignals = [
    candidate.length >= 12,
    PASSWORD_HAS_SYMBOL_PATTERN.test(candidate),
    PASSWORD_HAS_LOWER_PATTERN.test(candidate) && PASSWORD_HAS_UPPER_PATTERN.test(candidate),
  ].filter(Boolean).length;

  return {
    message: strengthSignals >= 2 ? 'Password strength: strong.' : 'Password strength: good.',
    tone: 'success',
    valid: true,
  };
};

const isEmailVerificationRequiredResponse = (data: EmbeddedAuthResponse): boolean => {
  const code = data.code?.toUpperCase();
  const message = data.message?.toLowerCase() || '';

  return (
    code === 'EMAIL_VERIFICATION_REQUIRED' ||
    code === 'EMAIL_NOT_VERIFIED' ||
    code === 'VERIFY_EMAIL' ||
    message.includes('verify your email') ||
    message.includes('email verification') ||
    message.includes('email is not verified')
  );
};

const getCredentialScope = (): string => {
  // Diagnostic form metadata only. Browser credential isolation follows origin/domain rules.
  const hostname = window.location.hostname.toLowerCase();

  if (hostname === 'botspot.trade' || hostname === 'www.botspot.trade') {
    return 'production';
  }

  if (hostname === 'dev.botspot.trade') {
    return 'development';
  }

  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1') {
    return 'local';
  }

  const safeHostname = hostname.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return safeHostname || 'app';
};

const getLoginFormAction = (scope = getCredentialScope()): string => {
  const actionUrl = new URL('/login', window.location.origin);
  actionUrl.searchParams.set('credential_scope', scope);
  return actionUrl.toString();
};

type PasswordCredentialPayload = {
  id: string;
  name?: string;
  password: string;
  iconURL?: string;
};

type PasswordCredentialConstructor = {
  new (form: HTMLFormElement): Credential;
  new (data: PasswordCredentialPayload): Credential;
};

type PreparedPasswordCredential = {
  credential: Credential | null;
  error: unknown;
};

type PendingMfaCredential = {
  data: PasswordCredentialPayload;
  prepared: PreparedPasswordCredential;
};

type PasswordCredentialCandidate =
  | { kind: 'prepared'; credential: Credential }
  | { kind: 'form'; form: HTMLFormElement }
  | { kind: 'payload'; payload: PasswordCredentialPayload };

const getPasswordCredentialConstructor = (): PasswordCredentialConstructor | undefined =>
  (
    window as typeof window & {
      PasswordCredential?: PasswordCredentialConstructor;
    }
  ).PasswordCredential;

const preparePasswordCredentialFromForm = (
  fallbackForm: HTMLFormElement | null,
): PreparedPasswordCredential => {
  const passwordCredentialConstructor = getPasswordCredentialConstructor();
  if (!fallbackForm || typeof passwordCredentialConstructor !== 'function') {
    return { credential: null, error: null };
  }

  try {
    return {
      credential: new passwordCredentialConstructor(fallbackForm),
      error: null,
    };
  } catch (credentialError) {
    return {
      credential: null,
      error: credentialError,
    };
  }
};

const storeEmbeddedPasswordCredential = async (
  credentialData: PasswordCredentialPayload,
  fallbackForm: HTMLFormElement | null,
  preparedCredential: PreparedPasswordCredential = { credential: null, error: null },
): Promise<void> => {
  const credentialsContainer = window.navigator?.credentials as
    | { store?: (credential: Credential) => Promise<Credential | null> }
    | undefined;
  const passwordCredentialConstructor = getPasswordCredentialConstructor();

  if (
    typeof credentialsContainer?.store !== 'function' ||
    typeof passwordCredentialConstructor !== 'function'
  ) {
    return;
  }

  const credentialInputs: PasswordCredentialCandidate[] = [
    ...(preparedCredential.credential
      ? [{ kind: 'prepared' as const, credential: preparedCredential.credential }]
      : []),
    ...(!preparedCredential.credential && fallbackForm
      ? [{ kind: 'form' as const, form: fallbackForm }]
      : []),
    { kind: 'payload', payload: credentialData },
  ];

  let lastError: unknown = preparedCredential.error;
  for (const credentialInput of credentialInputs) {
    try {
      const credential =
        credentialInput.kind === 'prepared'
          ? credentialInput.credential
          : credentialInput.kind === 'form'
            ? new passwordCredentialConstructor(credentialInput.form)
            : new passwordCredentialConstructor(credentialInput.payload);
      await credentialsContainer.store(credential);
      return;
    } catch (credentialError) {
      lastError = credentialError;
    }
  }

  if (lastError) {
    console.warn('[LoginRedirect] Could not store embedded password credential:', lastError);
  }
};

const isSafeInternalPath = (value: string): boolean =>
  value.startsWith('/') && !value.startsWith('//') && !value.startsWith('/\\');

const toInternalPath = (url: URL): string => `${url.pathname}${url.search}${url.hash}` || '/';

const normalizeReturnTo = (returnTo?: string | null): string => {
  const trimmedReturnTo = returnTo?.trim();
  if (!trimmedReturnTo) {
    return '/';
  }

  if (isSafeInternalPath(trimmedReturnTo) || /^https?:\/\//i.test(trimmedReturnTo)) {
    try {
      const parsed = new URL(trimmedReturnTo, window.location.origin);
      return parsed.origin === window.location.origin ? toInternalPath(parsed) : '/';
    } catch {
      return '/';
    }
  }

  return '/';
};

const prepareViewportForAuthNavigation = async (): Promise<void> => {
  const activeElement = document.activeElement;
  if (activeElement instanceof HTMLElement && activeElement.closest('.auth-email-form')) {
    activeElement.blur();
  }

  if (window.innerWidth > 768 || typeof window.requestAnimationFrame !== 'function') {
    return;
  }

  await new Promise<void>((resolve) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => resolve());
    });
  });
};

const extractActionAuthorizationId = (returnTo: string): string | null => {
  try {
    const parsed = new URL(returnTo, window.location.origin);
    const match = parsed.pathname.match(/^\/authorize-action\/([^/?#]+)$/);
    return match?.[1] || null;
  } catch {
    return null;
  }
};

const normalizeActionAuthorizationId = (value?: string | null): string | null => {
  const trimmedValue = value?.trim();
  if (!trimmedValue || trimmedValue.length > 200 || /[\\/?#]/.test(trimmedValue)) {
    return null;
  }
  return trimmedValue;
};

const SALES_SIGNUP_V4_LOGIN_ATTRIBUTION_KEYS = [
  '$feature/sales_page_signup_framing_v4_variant',
  '$feature_flag_sales_page_signup_framing_v4_variant',
  '$feature/sales_page_signup_framing_v5_variant',
  '$feature_flag_sales_page_signup_framing_v5_variant',
  '$feature/google_search_signup_experience_v1_variant',
  '$feature_flag_google_search_signup_experience_v1_variant',
  '$feature/google_search_signup_experience_v2_variant',
  '$feature_flag_google_search_signup_experience_v2_variant',
  '$feature/google_search_auth_first_personalization_2026_08_variant',
  '$feature_flag_google_search_auth_first_personalization_2026_08_variant',
  'feature_flag',
  'experiment_name',
  'experiment_variant',
  'sales_page_signup_framing_v4_experiment_name',
  'sales_page_signup_framing_v5_experiment_name',
  'sales_page_signup_framing_variant',
  'sales_page_signup_framing_v4_variant',
  'sales_page_signup_framing_v4_route_variant',
  'sales_page_signup_framing_v5_variant',
  'sales_page_signup_framing_v5_route_variant',
  'google_search_signup_experience_v1_experiment_name',
  'google_search_signup_experience_v1_variant',
  'google_search_signup_experience_v2_experiment_name',
  'google_search_signup_experience_v2_variant',
  'google_search_auth_first_personalization_2026_08_experiment_name',
  'google_search_auth_first_personalization_2026_08_variant',
  'google_search_lp_job',
  'sales_page_form_variant',
  'sales_page_framing_variant',
  'sales_page_design_variant',
] as const;

const getSalesSignupV4LoginAttribution = (returnTo: string): Record<string, string> => {
  try {
    const parsed = new URL(returnTo, window.location.origin);
    return SALES_SIGNUP_V4_LOGIN_ATTRIBUTION_KEYS.reduce<Record<string, string>>(
      (attribution, key) => {
        const value = parsed.searchParams.get(key);
        if (value) {
          attribution[key] = value;
        }
        return attribution;
      },
      {},
    );
  } catch {
    return {};
  }
};

const takeStoredRedirect = (): string | null => {
  try {
    const storedRedirect = window.localStorage.getItem('postLoginRedirect');
    window.localStorage.removeItem('postLoginRedirect');
    return storedRedirect;
  } catch {
    return null;
  }
};

const readResponseJson = async <T,>(response: Response): Promise<T> => {
  try {
    return await response.json();
  } catch {
    return {} as T;
  }
};

const readFormString = (form: HTMLFormElement, fieldName: string): string => {
  const value = new FormData(form).get(fieldName);
  return typeof value === 'string' ? value : '';
};

const waitForPasswordManagerCommit = async (
  form: HTMLFormElement,
  emailFieldName: string,
  passwordFieldName: string,
  timeoutMs: number,
): Promise<void> => {
  const deadline = Date.now() + timeoutMs;
  let emailObservedAt: number | null = null;

  while (Date.now() < deadline) {
    const emailValue = readFormString(form, emailFieldName).trim();
    const passwordValue = readFormString(form, passwordFieldName);

    if (emailValue) {
      emailObservedAt ??= Date.now();
      if (passwordValue || Date.now() - emailObservedAt >= PASSWORD_MANAGER_PASSWORD_SETTLE_MS) {
        return;
      }
    }

    await new Promise<void>((resolve) => {
      window.setTimeout(
        resolve,
        Math.min(PASSWORD_MANAGER_COMMIT_POLL_MS, Math.max(0, deadline - Date.now())),
      );
    });
  }
};

const isEmbeddedEmailAuthConfigured = (): boolean => {
  const apiBase = getApiBaseUrl().trim();
  if (!apiBase) {
    return false;
  }

  try {
    new URL(apiBase, window.location.origin);
    return true;
  } catch {
    return false;
  }
};

type PasswordFieldProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  error?: React.ReactNode;
  hideLabel: string;
  isVisible: boolean;
  label: React.ReactNode;
  onVisibleChange: React.Dispatch<React.SetStateAction<boolean>>;
  showLabel: string;
  toggleTabIndex?: number;
};

const PasswordField = React.forwardRef<HTMLInputElement, PasswordFieldProps>(function PasswordField(
  {
    className,
    error,
    hideLabel,
    id,
    isVisible,
    label,
    onVisibleChange,
    showLabel,
    toggleTabIndex,
    ...inputProps
  },
  ref,
) {
  const errorId = error && id ? `${id}-error` : undefined;
  const ToggleIcon = isVisible ? EyeOff : Eye;

  return (
    <div className="bsui-field">
      {label && (
        <label className="bsui-field__label" htmlFor={id}>
          {label}
        </label>
      )}
      <div className="auth-password-control">
        <Input
          {...inputProps}
          aria-describedby={errorId}
          aria-invalid={error ? true : undefined}
          className={cn('auth-password-control__input', className)}
          id={id}
          ref={ref}
          type={isVisible ? 'text' : 'password'}
        />
        <button
          aria-label={isVisible ? hideLabel : showLabel}
          aria-pressed={isVisible}
          className="auth-password-toggle"
          onClick={() => onVisibleChange((current) => !current)}
          onMouseDown={(event) => event.preventDefault()}
          tabIndex={toggleTabIndex}
          type="button"
        >
          <ToggleIcon aria-hidden="true" size={18} strokeWidth={2} />
        </button>
      </div>
      {error && (
        <span className="bsui-field__error" id={errorId}>
          {error}
        </span>
      )}
    </div>
  );
});

type LoginRedirectProps = {
  analyticsContext?: Record<string, unknown>;
  descriptionOverride?: string;
  forceFreshLoginOverride?: boolean;
  headingIdOverride?: string;
  headingOverride?: string;
  hideDescriptionOverride?: boolean;
  hideHeaderFromAssistiveTechOverride?: boolean;
  initialEmailOverride?: string;
  onAuthenticated?: () => void;
  newSignupReturnToOverride?: string;
  productLedOverride?: boolean;
  returnToOverride?: string;
  sourceOverride?: string;
};

const LoginRedirect = ({
  analyticsContext,
  descriptionOverride,
  forceFreshLoginOverride,
  headingIdOverride,
  headingOverride,
  hideDescriptionOverride,
  hideHeaderFromAssistiveTechOverride,
  initialEmailOverride,
  onAuthenticated,
  newSignupReturnToOverride,
  productLedOverride,
  returnToOverride,
  sourceOverride,
}: LoginRedirectProps = {}): React.ReactElement => {
  const {
    authSessionExchangeFailed,
    clearAuthSessionExchangeFailure,
    isAuthenticated,
    loginWithRedirect,
    isLoading,
    handleCustomJwtAuth,
    getApiToken,
  } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const securityIntent = params.get('security') === '1';
  const securityEmailSignIn =
    location.pathname === '/login' && securityIntent && params.get('signin') === 'email';
  const initialEmail =
    initialEmailOverride ?? params.get('login_hint') ?? params.get('email') ?? '';
  const [selectedProvider, setSelectedProvider] = useState<AuthProvider | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState(initialEmail);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailSuccess, setEmailSuccess] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState<string | null>(null);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [emailLookupSubmitting, setEmailLookupSubmitting] = useState(false);
  const [passwordResetSubmitting, setPasswordResetSubmitting] = useState(false);
  const [verificationResendSubmitting, setVerificationResendSubmitting] = useState(false);
  const [emailVerificationPending, setEmailVerificationPending] = useState(false);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const confirmPasswordInputRef = useRef<HTMLInputElement>(null);
  const focusPasswordAfterRevealRef = useRef(false);
  const focusEmailAfterUnlockRef = useRef(false);
  const credentialSyncFramesRef = useRef<number[]>([]);
  const credentialSyncTimersRef = useRef<number[]>([]);
  const userInitiatedEmailSubmitRef = useRef(false);
  const emailAuthIntentEmailRef = useRef<string | null>(null);
  const suppressCredentialAutorevealRef = useRef(false);
  const pendingMfaCredentialRef = useRef<PendingMfaCredential | null>(null);
  const mfaControllerRef = useRef<AbortController | null>(null);
  const [mfaChallenge, setMfaChallenge] = useState<string | null>(null);
  const [mfaMethod, setMfaMethod] = useState<'email' | 'sms' | undefined>();
  const [mfaCode, setMfaCode] = useState('');
  const [mfaUsesRecovery, setMfaUsesRecovery] = useState(false);
  const [recoveryResult, setRecoveryResult] = useState<EmbeddedAuthResponse | null>(null);
  const [mfaSubmitting, setMfaSubmitting] = useState(false);
  const [emailAuthStep, setEmailAuthStep] = useState<EmailAuthStep>(
    securityEmailSignIn && initialEmail ? 'password' : 'email',
  );
  useEffect(() => {
    if (!mfaChallenge) {
      pendingMfaCredentialRef.current = null;
      return;
    }
    const timeout = window.setTimeout(() => {
      pendingMfaCredentialRef.current = null;
      mfaControllerRef.current?.abort();
      setMfaChallenge(null);
      setMfaCode('');
      setRecoveryResult(null);
      setPassword('');
      setEmailAuthStep('email');
      setError('This sign-in has expired. Sign in again.');
    }, 5 * 60_000);
    return () => {
      window.clearTimeout(timeout);
      pendingMfaCredentialRef.current = null;
      mfaControllerRef.current?.abort();
    };
  }, [mfaChallenge]);
  const credentialScope = useMemo(() => getCredentialScope(), []);
  const loginFormAction = useMemo(() => getLoginFormAction(credentialScope), [credentialScope]);
  const embeddedEmailAuthAvailable = useMemo(() => isEmbeddedEmailAuthConfigured(), []);
  const socialAuthConfigured = Boolean(
    process.env.REACT_APP_AUTH0_DOMAIN && process.env.REACT_APP_AUTH0_CLIENT_ID,
  );
  const showSocialOptions = socialAuthConfigured && socialProviders.length > 0;

  const requestedReturnTo = returnToOverride ?? params.get('returnTo') ?? params.get('successUrl');
  const returnTo = normalizeReturnTo(requestedReturnTo || '/dashboard');
  const newSignupReturnTo = normalizeReturnTo(newSignupReturnToOverride || returnTo);
  const isFirstAccountSession = (data: EmbeddedAuthResponse) =>
    data.isNewUser === true && data.postSignupJourneyRequired === true;
  const acceptLogin = async (
    data: EmbeddedAuthResponse,
    signal?: AbortSignal,
    origin = 'embedded_email_signup',
  ) => {
    const startedJourney = isFirstAccountSession(data)
      ? startPostSignupJourney(data.user?.id, origin, newSignupReturnTo)
      : false;
    try {
      const accepted = data.securityAuthorization
        ? await handleCustomJwtAuth(data.token, signal, data.securityAuthorization)
        : signal
          ? await handleCustomJwtAuth(data.token, signal)
          : await handleCustomJwtAuth(data.token);
      if (!accepted && startedJourney) completePostSignupJourney();
      return accepted;
    } catch (error) {
      if (startedJourney) completePostSignupJourney();
      throw error;
    }
  };
  const destinationAfterVerifiedLogin = (data: EmbeddedAuthResponse) =>
    isFirstAccountSession(data)
      ? buildPostSignupDecisionPath(newSignupReturnTo, params.get('postSignupCheckoutVariant'))
      : returnTo === '/'
        ? '/dashboard'
        : returnTo;
  const salesSignupV4LoginAttribution = useMemo(
    () => getSalesSignupV4LoginAttribution(returnTo),
    [returnTo],
  );
  const [emailAuthIntent, setEmailAuthIntent] = useState<EmailAuthIntent>('signin');
  const loginFormAutocomplete = 'on';
  const emailInputAutocomplete = 'username';
  const emailAuthIntentMatchesEmail = emailAuthIntentEmailRef.current === email.trim();
  const emailInputName = 'username';
  const passwordInputName = 'password';
  const confirmPasswordInputName = 'password_confirmation';
  const passwordFieldsRevealed = emailAuthStep === 'password';
  const usesSignupPasswordField =
    emailAuthIntentMatchesEmail && emailAuthIntent === 'signup' && !emailVerificationPending;
  const showSignupControls = passwordFieldsRevealed && usesSignupPasswordField;
  const showPasswordResetAction =
    passwordFieldsRevealed &&
    emailAuthIntentMatchesEmail &&
    emailAuthIntent === 'signin' &&
    !emailVerificationPending;
  const emailInputErrorId = emailError ? `${emailInputName}-error` : undefined;
  const signupPasswordStrength = evaluateSignupPasswordStrength(password);
  const authStatus = error
    ? { message: error, role: 'alert' as const, tone: 'danger' as const }
    : emailSuccess
      ? { message: emailSuccess, role: 'status' as const, tone: 'success' as const }
      : authSessionExchangeFailed
        ? {
            message: 'Sign-in could not be completed. Try Google, Apple, or email again.',
            role: 'alert' as const,
            tone: 'danger' as const,
          }
        : null;
  const emailSubmitLoading = selectedProvider === 'email' || emailLookupSubmitting;
  const sourceFlow = sourceOverride ?? params.get('source') ?? 'login_route';
  const isLeadCaptureSurface = [
    'signup_auth_first',
    'google_search_auth_first',
    'google_search_generic_auth_first',
    'google_search_customized_auth_first',
    'sales_auth_first',
  ].includes(sourceFlow);
  const isProductLedAuth =
    productLedOverride ??
    ['sales_auth_first', 'checkout_auth_first', 'login_auth_first', 'signup_auth_first'].includes(
      sourceFlow,
    );
  const forceFreshLogin =
    forceFreshLoginOverride ??
    (params.get('freshLogin') === '1' ||
      params.get('fresh_login') === '1' ||
      params.get('prompt') === 'login' ||
      params.get('max_age') === '0');
  const highRiskStepUpActionId =
    forceFreshLogin && sourceFlow === 'action_authorization'
      ? normalizeActionAuthorizationId(params.get('actionId')) ||
        extractActionAuthorizationId(returnTo)
      : null;
  const googleSearchLeadAttribution = useMemo(
    () =>
      buildGoogleSearchLeadAttribution({
        context: analyticsContext,
        destination: newSignupReturnTo,
      }),
    [analyticsContext, newSignupReturnTo],
  );

  useEffect(() => {
    if (!initialEmail) {
      return;
    }

    setEmail((currentEmail) => currentEmail || initialEmail);
  }, [initialEmail]);

  useEffect(() => {
    pushEvent({
      ...(analyticsContext || {}),
      event: ANALYTICS_EVENTS.PAGE_VIEW,
      page_location: window.location.href,
      page_path: location.pathname,
      page_title: 'Sign in to BotSpot',
      source: sourceFlow,
      returnTo,
      auth_intent: 'continue',
      ...salesSignupV4LoginAttribution,
    });
  }, [analyticsContext, location.pathname, returnTo, salesSignupV4LoginAttribution, sourceFlow]);

  useEffect(() => {
    if (isLoading || !isAuthenticated) {
      return;
    }

    if (forceFreshLogin) {
      return;
    }

    if (hasPendingPostSignupJourney()) {
      onAuthenticated?.();
      navigate(buildPostSignupDecisionPath(newSignupReturnTo), { replace: true });
      return;
    }

    const redirectTo =
      returnToOverride || params.has('returnTo') || params.has('successUrl')
        ? returnTo
        : normalizeReturnTo(takeStoredRedirect() || '/dashboard');

    onAuthenticated?.();
    navigate(redirectTo, { replace: true });
  }, [
    forceFreshLogin,
    isAuthenticated,
    isLoading,
    navigate,
    newSignupReturnTo,
    onAuthenticated,
    params,
    returnTo,
    returnToOverride,
  ]);

  useEffect(() => {
    if (emailAuthStep === 'password') {
      if (focusPasswordAfterRevealRef.current) {
        focusPasswordAfterRevealRef.current = false;
        passwordInputRef.current?.focus({ preventScroll: true });
      }
    }
  }, [emailAuthStep]);

  useLayoutEffect(() => {
    if (emailAuthStep !== 'email' || !focusEmailAfterUnlockRef.current) return;
    focusEmailAfterUnlockRef.current = false;
    emailInputRef.current?.focus({ preventScroll: true });
    emailInputRef.current?.select();
  }, [emailAuthStep]);

  const syncCredentialInputsFromDom = useCallback(
    (options: { revealOnPassword?: boolean } = {}) => {
      const domEmail = emailInputRef.current?.value;
      const domPassword = passwordInputRef.current?.value;
      const domConfirmPassword = confirmPasswordInputRef.current?.value;

      if (typeof domEmail === 'string') {
        if (domEmail.trim()) {
          // Some password managers update native DOM after an early submit signal.
          // Clear validation created by that stale empty submit once credential lands.
          setEmailError((currentError) => (currentError ? null : currentError));
        }
        if (emailAuthStep === 'password') {
          if (emailInputRef.current && domEmail !== email) {
            emailInputRef.current.value = email;
          }
        } else {
          setEmail((currentEmail) => (currentEmail === domEmail ? currentEmail : domEmail));
        }
      }

      if (typeof domPassword === 'string') {
        setPassword((currentPassword) =>
          currentPassword === domPassword ? currentPassword : domPassword,
        );

        if (
          options.revealOnPassword &&
          domPassword &&
          emailAuthStep === 'email' &&
          !suppressCredentialAutorevealRef.current
        ) {
          focusPasswordAfterRevealRef.current = false;
          setEmailAuthIntent('signin');
          setEmailAuthStep('password');
          setPasswordError(null);
        }
      }

      if (typeof domConfirmPassword === 'string') {
        setConfirmPassword((currentConfirmPassword) =>
          currentConfirmPassword === domConfirmPassword
            ? currentConfirmPassword
            : domConfirmPassword,
        );
      }
    },
    [email, emailAuthStep],
  );

  const clearScheduledCredentialDomSync = useCallback(() => {
    credentialSyncFramesRef.current.forEach((frame) => window.cancelAnimationFrame(frame));
    credentialSyncTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    credentialSyncFramesRef.current = [];
    credentialSyncTimersRef.current = [];
  }, []);

  const scheduleCredentialDomSync = useCallback(
    (options: { revealOnPassword?: boolean } = {}) => {
      clearScheduledCredentialDomSync();
      syncCredentialInputsFromDom(options);

      const nextFrame = window.requestAnimationFrame(() => syncCredentialInputsFromDom(options));
      const secondFrame = window.requestAnimationFrame(() => {
        const nestedFrame = window.requestAnimationFrame(() =>
          syncCredentialInputsFromDom(options),
        );
        credentialSyncFramesRef.current.push(nestedFrame);
      });
      credentialSyncFramesRef.current.push(nextFrame, secondFrame);
      credentialSyncTimersRef.current.push(
        window.setTimeout(() => syncCredentialInputsFromDom(options), 50),
        window.setTimeout(() => syncCredentialInputsFromDom(options), 250),
        window.setTimeout(() => syncCredentialInputsFromDom(options), 750),
      );
    },
    [clearScheduledCredentialDomSync, syncCredentialInputsFromDom],
  );

  useEffect(() => {
    scheduleCredentialDomSync({ revealOnPassword: true });

    const syncAfterPageShow = () => scheduleCredentialDomSync({ revealOnPassword: true });
    const syncAfterVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        scheduleCredentialDomSync({ revealOnPassword: true });
      }
    };

    window.addEventListener('pageshow', syncAfterPageShow);
    document.addEventListener('visibilitychange', syncAfterVisibilityChange);
    return () => {
      window.removeEventListener('pageshow', syncAfterPageShow);
      document.removeEventListener('visibilitychange', syncAfterVisibilityChange);
      clearScheduledCredentialDomSync();
    };
  }, [clearScheduledCredentialDomSync, scheduleCredentialDomSync]);

  const revealPasswordFields = (options: { focusPassword?: boolean } = {}) => {
    suppressCredentialAutorevealRef.current = false;
    focusPasswordAfterRevealRef.current = Boolean(options.focusPassword);
    setEmailAuthStep('password');
  };

  const handleShowSignInOptions = () => {
    if (
      selectedProvider !== null ||
      emailLookupSubmitting ||
      passwordResetSubmitting ||
      verificationResendSubmitting ||
      emailAuthStep === 'email'
    ) {
      return false;
    }

    clearScheduledCredentialDomSync();
    suppressCredentialAutorevealRef.current = true;
    focusPasswordAfterRevealRef.current = false;
    setEmailAuthStep('email');
    setError(null);
    setEmailError(null);
    setEmailSuccess(null);
    setPasswordError(null);
    setConfirmPasswordError(null);
    setPasswordVisible(false);
    setConfirmPasswordVisible(false);
    setEmailVerificationPending(false);
    return true;
  };

  const handleChangeEmail = () => {
    if (handleShowSignInOptions()) focusEmailAfterUnlockRef.current = true;
  };

  const handleCredentialAutofillSignal = (event: React.AnimationEvent<HTMLInputElement>) => {
    if (event.animationName !== 'auth-autofill-start') {
      return;
    }

    scheduleCredentialDomSync({ revealOnPassword: true });

    const autofilledEmail = emailInputRef.current?.value.trim();
    if (
      event.currentTarget === emailInputRef.current &&
      autofilledEmail &&
      emailAuthStep === 'email' &&
      !suppressCredentialAutorevealRef.current
    ) {
      setEmail(autofilledEmail);
      setEmailError(null);
      setPasswordError(null);
      setConfirmPasswordError(null);
      setEmailVerificationPending(false);
      setEmailAuthIntent('signin');
      revealPasswordFields({ focusPassword: false });
      window.setTimeout(() => scheduleCredentialDomSync({ revealOnPassword: true }), 0);
      window.setTimeout(() => scheduleCredentialDomSync({ revealOnPassword: true }), 120);
    }
  };

  const startHostedLogin = (
    connection: string,
    options: { loginHint?: string; requireFresh?: boolean } = {},
  ) => {
    rememberPendingPostSignupDestination(newSignupReturnTo);
    // The callback reads these notes from sessionStorage. An ordinary sign-in
    // must clear notes left by an earlier, abandoned approval or security
    // re-check, or the callback treats it as that re-check and rejects it.
    if (highRiskStepUpActionId) rememberHighRiskStepUpActionId(highRiskStepUpActionId);
    else clearHighRiskStepUpActionId();
    if (!securityIntent) clearSecurityReauthIntent();
    const fresh = forceFreshLogin || options.requireFresh;
    const authorizationParams: AuthorizationParams = {
      redirect_uri: getAuth0RedirectOrigin(window.location),
      scope: 'openid profile email offline_access',
      connection,
      prompt: fresh ? 'login' : 'select_account',
    };
    if (securityIntent) {
      rememberSecurityReauthIntent();
      authorizationParams.audience = getMyAccountAudience(process.env.REACT_APP_AUTH0_DOMAIN || '');
      authorizationParams.scope = `openid profile email ${MY_ACCOUNT_SCOPES}`;
    }
    if (fresh) authorizationParams.max_age = 0;
    if (securityIntent || highRiskStepUpActionId) {
      authorizationParams.acr_values = highRiskStepUpActionId
        ? 'http://schemas.openid.net/pape/policies/2007/06/multi-factor'
        : 'https://botspot.trade/assurance/account-security';
    }
    if (options.loginHint) authorizationParams.login_hint = options.loginHint;
    return loginWithRedirect({
      appState: { returnTo },
      authorizationParams,
    });
  };

  const handleNativePasskeyLogin = async () => {
    try {
      // Native exchange carries its intent in the request itself. Clear notes
      // belonging to an abandoned hosted callback before starting this login.
      clearHighRiskStepUpActionId();
      clearSecurityReauthIntent();
      rememberPendingPostSignupDestination(newSignupReturnTo);
      const accountToken = securityIntent ? await getApiToken() : undefined;
      if (securityIntent && !accountToken) {
        throw new PasskeyLoginError(
          'Sign in with email or Google before changing security settings.',
        );
      }
      const session = await loginWithPasskey(
        getApiBaseUrl().trim(),
        highRiskStepUpActionId,
        undefined,
        accountToken ? { accountToken } : undefined,
      );
      if (!(await acceptLogin(session, undefined, 'passkey_signup'))) {
        setError('We could not start your session. Please try again.');
        return;
      }
      await prepareViewportForAuthNavigation();
      onAuthenticated?.();
      navigate(destinationAfterVerifiedLogin(session), { replace: true });
    } catch (passkeyError) {
      const cancelled =
        passkeyError instanceof DOMException &&
        (passkeyError.name === 'NotAllowedError' || passkeyError.name === 'AbortError');
      if (!cancelled) {
        console.error('[LoginRedirect] Passkey sign-in failed:', passkeyError);
        setError(
          passkeyError instanceof PasskeyLoginError
            ? passkeyError.message
            : 'Passkey sign-in did not finish. Try again, or use email or Google.',
        );
      }
    } finally {
      setSelectedProvider(null);
    }
  };

  const handleProviderClick = async (provider: HostedProvider, connection: string) => {
    if (!socialAuthConfigured) {
      setError('Sign-in is temporarily unavailable. Please try again.');
      return;
    }

    setSelectedProvider(provider);
    setError(null);

    if (isLeadCaptureSurface) {
      // A social-account submit on a lead-capture surface is one generic Lead.
      trackLeadSubmit();
    }

    pushEvent({
      ...(analyticsContext || {}),
      event: ANALYTICS_EVENTS.LOGIN_CLICKED,
      method: providerEventMethod[provider],
      source: sourceFlow,
      returnTo,
      auth_surface: 'unified_login',
      auth_intent: sourceFlow === 'signup_auth_first' ? 'signup' : 'continue',
      ...salesSignupV4LoginAttribution,
    });

    if (provider === 'passkey') {
      await handleNativePasskeyLogin();
      return;
    }

    try {
      await startHostedLogin(connection);
    } catch (loginError) {
      console.error('[LoginRedirect] Login redirect failed:', loginError);
      setSelectedProvider(null);
      setError('We could not start sign in. Please try again.');
    }
  };

  const resolveEmailAuthIntent = async (trimmedEmail: string): Promise<EmailAuthIntent | null> => {
    if (!embeddedEmailAuthAvailable) {
      setError(DISABLED_EMAIL_AUTH_MESSAGE);
      setEmailVerificationPending(false);
      return null;
    }

    const intentPreviouslyResolvedForEmail = emailAuthIntentEmailRef.current === trimmedEmail;

    try {
      const response = await fetchWithTimeout(
        embeddedAuthEndpoint('account-status'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email: trimmedEmail }),
        },
        AUTH_REQUEST_TIMEOUT_MS,
      );
      const data = await readResponseJson<EmbeddedAccountStatusResponse>(response);
      if (!response.ok) {
        setError(data.message || ACCOUNT_STATUS_UNAVAILABLE_MESSAGE);
        setEmailVerificationPending(false);
        return null;
      }
      const resolvedIntent = data.exists === false ? 'signup' : 'signin';
      clearAuthSessionExchangeFailure?.();
      setError(null);
      emailAuthIntentEmailRef.current = trimmedEmail;
      setEmailAuthIntent(resolvedIntent);
      if (resolvedIntent === 'signup' && !intentPreviouslyResolvedForEmail) {
        // Never carry a saved sign-in password into a newly discovered signup flow.
        setPassword('');
        setPasswordVisible(false);
      }
      setEmailVerificationPending(false);
      return resolvedIntent;
    } catch (accountStatusError) {
      console.warn('[LoginRedirect] Account status lookup failed:', accountStatusError);
      setError(ACCOUNT_STATUS_UNAVAILABLE_MESSAGE);
      setEmailVerificationPending(false);
      return null;
    }
  };

  const moveEmailAuthToVerificationPending = (
    trimmedEmail: string,
    status: { error?: string; success?: string } = { success: EMAIL_VERIFICATION_REQUIRED_MESSAGE },
  ) => {
    // Verification pending means ownership is not proven yet; do not frame it as a bad password.
    clearAuthSessionExchangeFailure?.();
    clearScheduledCredentialDomSync();
    suppressCredentialAutorevealRef.current = true;
    setEmailAuthIntent('signin');
    emailAuthIntentEmailRef.current = trimmedEmail;
    setPassword('');
    setConfirmPassword('');
    setPasswordVisible(false);
    setConfirmPasswordVisible(false);
    setPasswordError(null);
    setConfirmPasswordError(null);
    setError(status.error || null);
    setEmailVerificationPending(true);
    setEmailSuccess(status.success || null);
  };

  const handleEmailAuth = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const submittedForm = event.currentTarget;
    const nativeSubmitter = (event.nativeEvent as SubmitEvent).submitter;
    const userInitiatedSubmit = userInitiatedEmailSubmitRef.current || Boolean(nativeSubmitter);
    userInitiatedEmailSubmitRef.current = false;

    let formEmail = readFormString(submittedForm, emailInputName);
    let formPassword = readFormString(submittedForm, passwordInputName);
    let formConfirmPassword = readFormString(submittedForm, confirmPasswordInputName);

    if (!formEmail.trim() && !email.trim()) {
      // iCloud Passwords can signal submit before committing native input values.
      // A submit without an explicit submitter is the extension ordering seen in
      // the wild. Poll longer for that path while keeping normal empty submits fast.
      const commitTimeoutMs = userInitiatedSubmit
        ? PASSWORD_MANAGER_COMMIT_GRACE_MS
        : PASSWORD_MANAGER_DELAYED_COMMIT_TIMEOUT_MS;
      scheduleCredentialDomSync({ revealOnPassword: true });
      await waitForPasswordManagerCommit(
        submittedForm,
        emailInputName,
        passwordInputName,
        commitTimeoutMs,
      );
      formEmail = readFormString(submittedForm, emailInputName);
      formPassword = readFormString(submittedForm, passwordInputName);
      formConfirmPassword = readFormString(submittedForm, confirmPasswordInputName);
    }

    const trimmedEmail = (formEmail || email).trim();
    const submittedPassword = formPassword || password;
    const submittedConfirmPassword = formConfirmPassword || confirmPassword;
    const nextEmailError = trimmedEmail ? null : 'Enter your email.';

    setEmailError(nextEmailError);
    setEmailSuccess(null);
    setEmailVerificationPending(false);
    setEmail(trimmedEmail);
    setPassword(submittedPassword);
    setConfirmPassword(submittedConfirmPassword);

    if (nextEmailError) {
      emailInputRef.current?.focus({ preventScroll: true });
      return;
    }

    if (emailAuthStep === 'email') {
      if (isLeadCaptureSurface) {
        // A valid email submit is the Lead. Pressing Enter and clicking the
        // button both use this form handler.
        trackLeadSubmit();
      }
      setError(null);
      setPasswordError(null);
      setConfirmPasswordError(null);
      if (!submittedPassword || emailAuthIntentEmailRef.current !== trimmedEmail) {
        setEmailLookupSubmitting(true);
        try {
          const resolvedIntent = await resolveEmailAuthIntent(trimmedEmail);
          if (!resolvedIntent) {
            return;
          }
        } finally {
          setEmailLookupSubmitting(false);
        }
      }

      revealPasswordFields({ focusPassword: !submittedPassword });
      return;
    }

    const nextPasswordError = submittedPassword ? null : 'Enter your password.';
    setPasswordError(nextPasswordError);
    if (nextPasswordError) {
      return;
    }

    if (embeddedEmailAuthAvailable && typeof handleCustomJwtAuth !== 'function') {
      setError('Email authentication is unavailable in this app build.');
      return;
    }

    let resolvedEmailAuthIntent = emailAuthIntent;

    if (embeddedEmailAuthAvailable && emailAuthIntentEmailRef.current !== trimmedEmail) {
      setEmailLookupSubmitting(true);
      try {
        const resolvedIntent = await resolveEmailAuthIntent(trimmedEmail);
        if (!resolvedIntent) {
          return;
        }
        resolvedEmailAuthIntent = resolvedIntent;
      } finally {
        setEmailLookupSubmitting(false);
      }
    }

    if (resolvedEmailAuthIntent === 'signup') {
      const signupStrength = evaluateSignupPasswordStrength(submittedPassword);
      const nextSignupPasswordError = signupStrength.valid
        ? null
        : SIGNUP_PASSWORD_REQUIREMENT_MESSAGE;
      const nextConfirmPasswordError = submittedConfirmPassword
        ? submittedConfirmPassword === submittedPassword
          ? null
          : 'Passwords do not match.'
        : 'Repeat your password.';

      setPasswordError(nextSignupPasswordError);
      setConfirmPasswordError(nextConfirmPasswordError);

      if (nextSignupPasswordError || nextConfirmPasswordError) {
        revealPasswordFields();
        return;
      }
    } else {
      setConfirmPasswordError(null);
    }

    const preparedPasswordCredential = preparePasswordCredentialFromForm(submittedForm);

    setSelectedProvider('email');
    setError(null);

    pushEvent({
      ...(analyticsContext || {}),
      event: ANALYTICS_EVENTS.LOGIN_CLICKED,
      method: embeddedEmailAuthAvailable ? 'embedded_email' : providerEventMethod.email,
      source: sourceFlow,
      returnTo,
      auth_surface: 'unified_login',
      auth_intent: resolvedEmailAuthIntent,
      ...salesSignupV4LoginAttribution,
    });

    let postSignupJourneyStarted = false;
    try {
      const requestBody: EmbeddedAuthRequest = {
        email: trimmedEmail,
        password: submittedPassword,
      };
      if (highRiskStepUpActionId) requestBody.highRiskStepUpActionId = highRiskStepUpActionId;
      clearSecurityReauthIntent();
      if (securityIntent) {
        requestBody.security = true;
        requestBody.securityAccountToken = await getApiToken();
      }
      // Every signup carries the stored campaign link and its first touch
      // (utm_content is the YouTube video id for Media Studio links), so Node
      // can trace this account's later payments to that video (Rob, 2026-10-21).
      // Before this only Google Search visitors sent it.
      const campaignLink: Record<string, string> | null = getUtmParamsForApi();
      if (
        resolvedEmailAuthIntent === 'signup' &&
        (campaignLink || googleSearchLeadAttribution || getWebsiteThemeAttribution())
      ) {
        requestBody.leadAttribution = {
          ...campaignLink,
          ...googleSearchLeadAttribution,
          ...getWebsiteThemeAttribution(),
        };
      }
      // Node stores it as user.signupTimeZone only at the first verified
      // signup, so the AI welcome call stays inside legal calling hours.
      const signupTimeZone = getBrowserTimeZone();
      if (signupTimeZone) requestBody.signupTimeZone = signupTimeZone;
      const response = await fetchWithTimeout(
        embeddedAuthEndpoint(resolvedEmailAuthIntent === 'signup' ? 'signup' : 'login'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        },
        AUTH_REQUEST_TIMEOUT_MS,
      );

      const data = await readResponseJson<EmbeddedAuthResponse>(response);

      if (!response.ok) {
        if (data.code === 'AUTH0_MFA_REQUIRED') {
          if (!data.mfaChallenge) {
            setError('Verification is temporarily unavailable. Try again shortly.');
            return;
          }
          clearScheduledCredentialDomSync();
          suppressCredentialAutorevealRef.current = true;
          pendingMfaCredentialRef.current = {
            data: { id: trimmedEmail, name: trimmedEmail, password: submittedPassword },
            prepared: preparedPasswordCredential,
          };
          setMfaChallenge(data.mfaChallenge);
          setMfaMethod(data.mfaMethod);
          setMfaUsesRecovery(false);
          setRecoveryResult(null);
          setMfaCode('');
          setPassword('');
          setError(null);
          clearAuthSessionExchangeFailure?.();
          return;
        }
        if (data.code === 'AUTH0_PASSWORD_GRANT_DISABLED') {
          setError(DISABLED_EMAIL_AUTH_MESSAGE);
          return;
        }

        if (data.code === 'ACCOUNT_EXISTS' && resolvedEmailAuthIntent === 'signup') {
          clearAuthSessionExchangeFailure?.();
          setEmailAuthIntent('signin');
          emailAuthIntentEmailRef.current = trimmedEmail;
          setPasswordError(null);
          setConfirmPassword('');
          setConfirmPasswordError(null);
          setEmailVerificationPending(false);
          setEmailSuccess('Continue with your password.');
          return;
        }

        if (isEmailVerificationRequiredResponse(data)) {
          moveEmailAuthToVerificationPending(trimmedEmail);
          return;
        }

        if (data.code === 'VERIFICATION_EMAIL_SEND_FAILED') {
          moveEmailAuthToVerificationPending(trimmedEmail, {
            error: data.message || 'Verification email could not be sent. Try resend.',
          });
          return;
        }

        setError(
          data.message ||
            (resolvedEmailAuthIntent === 'signup'
              ? 'We could not create that account. Please try again.'
              : 'We could not sign you in. Check your email and password.'),
        );
        return;
      }

      if (data.requiresLogin) {
        moveEmailAuthToVerificationPending(trimmedEmail, {
          success: data.verificationEmailSent
            ? data.message || VERIFICATION_EMAIL_SENT_MESSAGE
            : EMAIL_VERIFICATION_REQUIRED_MESSAGE,
        });
        return;
      }

      if (!data.token) {
        setError('Authentication completed without a session. Please try again.');
        return;
      }

      // The successful signup endpoint is already authoritative evidence that
      // this browser just created an account. Older Node candidates do not
      // always include the optional journey flags, so missing flags must not
      // let a new account bypass attribution and the post-signup decision.
      // Sign-in can create the account too: someone who signed up by email,
      // verified the address and then signed in is new. Node says so with
      // isNewUser + postSignupJourneyRequired, on either form (Rob,
      // 2026-10-20: these users skipped the survey, checkout and landing).
      const requiresPostSignupJourney =
        isFirstAccountSession(data) ||
        (resolvedEmailAuthIntent === 'signup' &&
          data.postSignupJourneyRequired !== false &&
          data.isNewUser !== false);
      if (requiresPostSignupJourney && !isFirstAccountSession(data)) {
        postSignupJourneyStarted = startPostSignupJourney(
          data.user?.id,
          'embedded_email_signup',
          newSignupReturnTo,
        );
      }

      const accepted = await acceptLogin(data);
      if (!accepted) {
        if (postSignupJourneyStarted) {
          completePostSignupJourney();
        }
        setError('We could not start your session. Please try again.');
        return;
      }
      postSignupJourneyStarted = false;

      await storeEmbeddedPasswordCredential(
        {
          id: trimmedEmail,
          name: trimmedEmail,
          password: submittedPassword,
        },
        submittedForm,
        preparedPasswordCredential,
      );
      await prepareViewportForAuthNavigation();
      onAuthenticated?.();
      if (requiresPostSignupJourney) {
        navigate(
          buildPostSignupDecisionPath(newSignupReturnTo, params.get('postSignupCheckoutVariant')),
          { replace: true },
        );
      } else {
        // Existing users who sign in from the public root belong in their
        // workspace. Navigate there directly instead of relying on a second
        // root-route auth render, which can briefly resolve the public page.
        navigate(
          resolvedEmailAuthIntent === 'signin' && returnTo === '/' ? '/dashboard' : returnTo,
          { replace: true },
        );
      }
    } catch (emailAuthError) {
      if (postSignupJourneyStarted) {
        completePostSignupJourney();
      }
      console.error('[LoginRedirect] Email authentication failed:', emailAuthError);
      setError(
        emailAuthError instanceof DOMException && emailAuthError.name === 'TimeoutError'
          ? 'Email authentication timed out. Please try again.'
          : 'We could not reach authentication. Please try again.',
      );
    } finally {
      setSelectedProvider(null);
    }
  };

  const cancelMfa = () => {
    setRecoveryResult(null);
    setMfaUsesRecovery(false);
    setMfaChallenge(null);
    setMfaCode('');
    setPassword('');
    setError(null);
    handleShowSignInOptions();
  };

  const verifyMfa = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!mfaChallenge || mfaSubmitting) return;
    const codeValue = new FormData(event.currentTarget).get(
      mfaUsesRecovery ? 'recovery-code' : 'one-time-code',
    );
    const parsedCode = z.string().safeParse(codeValue);
    const submittedCode = parsedCode.success ? parsedCode.data.trim() : '';
    if (!recoveryResult && (mfaUsesRecovery ? !submittedCode : !/^[0-9]{6}$/.test(submittedCode))) {
      setMfaCode('');
      setError(mfaUsesRecovery ? 'Enter your recovery code.' : 'Enter the 6-digit code.');
      return;
    }
    const controller = new AbortController();
    mfaControllerRef.current = controller;
    const pendingCredential = pendingMfaCredentialRef.current;
    setMfaSubmitting(true);
    setError(null);
    try {
      let data = recoveryResult;
      if (!data) {
        const response = await fetchWithTimeout(
          embeddedAuthEndpoint('mfa/verify'),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              mfaChallenge,
              ...(mfaUsesRecovery ? { recoveryCode: submittedCode } : { otp: submittedCode }),
            }),
            signal: controller.signal,
          },
          AUTH_REQUEST_TIMEOUT_MS,
        );
        data = await readResponseJson<EmbeddedAuthResponse>(response);
        if (controller.signal.aborted) return;
        if (!response.ok) {
          if (data.code === 'MFA_CHALLENGE_EXPIRED') {
            cancelMfa();
          }
          setError(data.message || 'Verification could not be completed. Try again.');
          return;
        }
        if (data.token && data.recoveryCode) {
          setRecoveryResult(data);
          setMfaCode('');
          return;
        }
      }
      if (controller.signal.aborted) return;
      if (!data.token || !(await acceptLogin(data, controller.signal))) {
        setError('We could not start your session. Sign in again.');
        return;
      }
      if (pendingCredential)
        await storeEmbeddedPasswordCredential(
          pendingCredential.data,
          null,
          pendingCredential.prepared,
        );
      pendingMfaCredentialRef.current = null;
      setRecoveryResult(null);
      setMfaChallenge(null);
      setMfaCode('');
      await prepareViewportForAuthNavigation();
      onAuthenticated?.();
      navigate(destinationAfterVerifiedLogin(data), { replace: true });
    } catch {
      if (controller.signal.aborted) return;
      setError('Verification could not be reached. Try again.');
    } finally {
      if (mfaControllerRef.current === controller) mfaControllerRef.current = null;
      setMfaSubmitting(false);
    }
  };

  const handleEmailFormKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      event.key !== 'Enter' ||
      event.nativeEvent.isComposing ||
      selectedProvider !== null ||
      emailLookupSubmitting ||
      passwordResetSubmitting ||
      verificationResendSubmitting
    ) {
      return;
    }

    const form = event.currentTarget.form;
    if (!form?.requestSubmit) {
      return;
    }

    event.preventDefault();
    userInitiatedEmailSubmitRef.current = true;
    form.requestSubmit();
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextPassword = event.target.value;
    setPassword(nextPassword);
    if (nextPassword && !passwordFieldsRevealed) {
      setEmailAuthIntent('signin');
      revealPasswordFields();
    }
    if (nextPassword) {
      setEmailVerificationPending(false);
    }
    if (passwordError) {
      setPasswordError(null);
    }
    if (confirmPasswordError) {
      setConfirmPasswordError(null);
    }
  };

  const handlePasswordReset = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setEmailError('Enter your email first.');
      setEmailSuccess(null);
      return;
    }

    setEmailError(null);
    setEmailSuccess(null);
    setError(null);
    setPasswordResetSubmitting(true);

    try {
      const response = await fetchWithTimeout(
        embeddedAuthEndpoint('password-reset'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email: trimmedEmail }),
        },
        AUTH_REQUEST_TIMEOUT_MS,
      );
      const data = await readResponseJson<EmbeddedAuthResponse>(response);

      if (!response.ok) {
        setError(data.message || 'We could not start password reset. Please try again.');
        return;
      }

      clearAuthSessionExchangeFailure?.();
      setError(null);
      setEmailSuccess('If an account exists for that email, reset instructions will be sent.');
    } catch (passwordResetError) {
      console.error('[LoginRedirect] Password reset failed:', passwordResetError);
      setError('We could not start password reset. Please try again.');
    } finally {
      setPasswordResetSubmitting(false);
    }
  };

  const handleVerificationResend = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setEmailError('Enter your email first.');
      setEmailSuccess(null);
      return;
    }

    setEmailError(null);
    setEmailSuccess(null);
    setError(null);
    setVerificationResendSubmitting(true);

    try {
      const response = await fetchWithTimeout(
        embeddedAuthEndpoint('resend-verification'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email: trimmedEmail }),
        },
        AUTH_REQUEST_TIMEOUT_MS,
      );
      const data = await readResponseJson<EmbeddedAuthResponse>(response);

      if (!response.ok) {
        setError(data.message || 'Verification email could not be sent. Try again shortly.');
        return;
      }

      clearAuthSessionExchangeFailure?.();
      setError(null);
      setEmailSuccess(data.message || VERIFICATION_EMAIL_SENT_MESSAGE);
    } catch (verificationResendError) {
      console.error('[LoginRedirect] Verification email resend failed:', verificationResendError);
      setError('Verification email could not be sent. Try again shortly.');
    } finally {
      setVerificationResendSubmitting(false);
    }
  };

  return (
    <div className="auth-shell auth-shell--centered">
      <Panel
        className={cn(
          'auth-panel auth-panel--signin',
          passwordFieldsRevealed && 'auth-panel--password',
          isProductLedAuth && 'auth-panel--sales',
        )}
      >
        <div
          aria-hidden={hideHeaderFromAssistiveTechOverride || undefined}
          className="auth-panel__header"
        >
          {isProductLedAuth ? (
            <>
              <h2 id={headingIdOverride}>{headingOverride || 'Create your BotSpot account'}</h2>
              {!hideDescriptionOverride && (
                <p>
                  {descriptionOverride ||
                    'Use Google, Apple, or email. No payment details required.'}
                </p>
              )}
            </>
          ) : (
            <>
              <h1 id={headingIdOverride}>
                {headingOverride || (showSignupControls ? 'Sign Up' : 'Sign in to BotSpot')}
              </h1>
              {!hideDescriptionOverride && (
                <p>{descriptionOverride || 'Use Google, Apple, or email to continue.'}</p>
              )}
            </>
          )}
          {passwordFieldsRevealed && <span aria-hidden="true" className="auth-header-divider" />}
        </div>

        <div
          className={cn('auth-entry-stack', passwordFieldsRevealed && 'auth-entry-stack--password')}
          data-state={passwordFieldsRevealed ? 'password' : 'choice'}
        >
          {showSocialOptions && (
            <div
              hidden={Boolean(mfaChallenge)}
              aria-hidden={passwordFieldsRevealed || Boolean(mfaChallenge)}
              className={cn(
                'auth-social-options',
                passwordFieldsRevealed
                  ? 'auth-social-options--hidden'
                  : 'auth-social-options--visible',
              )}
              data-state={passwordFieldsRevealed ? 'hidden' : 'visible'}
            >
              <div className="auth-provider-stack">
                {socialProviders.map(({ provider, label, icon, connection }) => (
                  <Button
                    key={provider}
                    className="auth-provider-button"
                    disabled={isLoading || selectedProvider !== null}
                    fullWidth
                    icon={
                      <span
                        aria-hidden="true"
                        className={cn('auth-provider-mark', `auth-provider-mark--${provider}`)}
                      >
                        {icon}
                      </span>
                    }
                    loading={selectedProvider === provider}
                    onClick={() => handleProviderClick(provider, connection)}
                    size="lg"
                    tabIndex={passwordFieldsRevealed ? -1 : undefined}
                    variant="secondary"
                  >
                    {label}
                  </Button>
                ))}
              </div>

              <div className="auth-divider" role="separator">
                <span>or</span>
              </div>
            </div>
          )}

          {mfaChallenge && (
            <form
              onSubmit={verifyMfa}
              className="auth-email-form ph-no-capture"
              id="botspot-mfa-form"
              name="botspot-mfa-form"
              autoComplete="on"
              aria-label="Verify your identity"
            >
              <input type="hidden" name="username" autoComplete="username" value={email} readOnly />
              <h3>Verify your identity</h3>
              {recoveryResult ? (
                <>
                  <h4>Save your new recovery code</h4>
                  <p>Your old recovery code has been used. Save this replacement somewhere safe.</p>
                  <code className="ph-no-capture">{recoveryResult.recoveryCode}</code>
                  <Button fullWidth type="submit" loading={mfaSubmitting}>
                    I saved it, continue
                  </Button>
                </>
              ) : (
                <>
                  <p>
                    {mfaUsesRecovery
                      ? 'Enter the recovery code you saved when setting up two-factor authentication.'
                      : mfaMethod === 'email'
                        ? 'Enter the 6-digit code sent to your email.'
                        : mfaMethod === 'sms'
                          ? 'Enter the 6-digit code sent to your phone.'
                          : 'Enter the 6-digit code from your authenticator app.'}
                  </p>
                  <label htmlFor="login-mfa-code">
                    {mfaUsesRecovery
                      ? 'Recovery code'
                      : mfaMethod === 'email'
                        ? 'Email code'
                        : mfaMethod === 'sms'
                          ? 'Text message code'
                          : 'Authenticator code'}
                  </label>
                  <Input
                    id="login-mfa-code"
                    name={mfaUsesRecovery ? 'recovery-code' : 'one-time-code'}
                    type="text"
                    inputMode={mfaUsesRecovery ? 'text' : 'numeric'}
                    autoComplete={mfaUsesRecovery ? 'off' : 'one-time-code'}
                    pattern={mfaUsesRecovery ? undefined : '[0-9]{6}'}
                    maxLength={mfaUsesRecovery ? 128 : 6}
                    required
                    autoFocus
                    value={mfaCode}
                    onInvalid={(event) => {
                      if (!mfaUsesRecovery) {
                        event.preventDefault();
                        setMfaCode('');
                        setError('Enter the 6-digit code.');
                      }
                    }}
                    onChange={(event) => {
                      const value = event.target.value;
                      if (!mfaUsesRecovery && !/^[0-9]{0,6}$/.test(value)) {
                        setMfaCode('');
                        setError('Enter the 6-digit code.');
                        return;
                      }
                      setMfaCode(value);
                      setError(null);
                    }}
                  />
                  <Button
                    fullWidth
                    type="submit"
                    loading={mfaSubmitting}
                    disabled={
                      mfaSubmitting ||
                      (mfaUsesRecovery ? !mfaCode.trim() : !/^[0-9]{6}$/.test(mfaCode))
                    }
                  >
                    Verify and sign in
                  </Button>
                  <Button
                    type="button"
                    variant="link"
                    disabled={mfaSubmitting}
                    onClick={() => {
                      setMfaUsesRecovery(!mfaUsesRecovery);
                      setMfaCode('');
                      setError(null);
                    }}
                  >
                    {mfaUsesRecovery
                      ? mfaMethod === 'email'
                        ? 'Use email code'
                        : mfaMethod === 'sms'
                          ? 'Use text message code'
                          : 'Use authenticator app'
                      : 'Use a recovery code'}
                  </Button>
                </>
              )}
              {error && (
                <div role="alert" className="auth-form-status auth-form-status--danger">
                  {error}
                </div>
              )}
              <Button type="button" disabled={mfaSubmitting} onClick={cancelMfa}>
                Cancel verification
              </Button>
            </form>
          )}
          {!mfaChallenge && (
            <form
              action={loginFormAction}
              autoComplete={loginFormAutocomplete}
              className={cn(
                'auth-email-form',
                !passwordFieldsRevealed && 'auth-email-form--password-collapsed',
              )}
              data-credential-scope={credentialScope}
              id="botspot-credential-form"
              method="post"
              name="botspot-credential-form"
              noValidate
              onSubmit={handleEmailAuth}
            >
              <div className="auth-email-form__fields">
                <div className="bsui-field auth-email-field">
                  <div className="auth-email-field__label-row">
                    <label className="bsui-field__label" htmlFor={emailInputName}>
                      Email
                    </label>
                  </div>
                  <div className="auth-email-control">
                    <Input
                      aria-describedby={emailInputErrorId}
                      aria-invalid={emailError ? true : undefined}
                      autoCapitalize="none"
                      autoComplete={emailInputAutocomplete}
                      autoCorrect="off"
                      className={cn(passwordFieldsRevealed && 'auth-email-input--locked')}
                      id={emailInputName}
                      inputMode="email"
                      name={emailInputName}
                      ref={emailInputRef}
                      onChange={(event) => {
                        if (passwordFieldsRevealed) {
                          event.currentTarget.value = email;
                          return;
                        }
                        if (event.target.value.trim() !== emailAuthIntentEmailRef.current) {
                          emailAuthIntentEmailRef.current = null;
                          setConfirmPassword('');
                          setConfirmPasswordError(null);
                        }
                        setEmailVerificationPending(false);
                        setEmail(event.target.value);
                        if (emailError) {
                          setEmailError(null);
                        }
                        scheduleCredentialDomSync({ revealOnPassword: true });
                      }}
                      onAnimationStart={handleCredentialAutofillSignal}
                      onInput={(event) => {
                        if (passwordFieldsRevealed) {
                          event.currentTarget.value = email;
                          return;
                        }
                        scheduleCredentialDomSync({ revealOnPassword: true });
                      }}
                      onKeyDown={handleEmailFormKeyDown}
                      placeholder="you@example.com"
                      readOnly={passwordFieldsRevealed}
                      required
                      spellCheck={false}
                      type="email"
                      value={email}
                    />
                    {passwordFieldsRevealed && (
                      <button
                        aria-label="Change email"
                        className="auth-email-edit-button"
                        disabled={
                          selectedProvider !== null ||
                          emailLookupSubmitting ||
                          passwordResetSubmitting
                        }
                        onClick={handleChangeEmail}
                        type="button"
                      >
                        <Pencil aria-hidden="true" size={16} strokeWidth={2} />
                      </button>
                    )}
                  </div>
                  {emailError && (
                    <span className="bsui-field__error" id={emailInputErrorId} role="alert">
                      {emailError}
                    </span>
                  )}
                </div>
                <div
                  aria-hidden={!passwordFieldsRevealed}
                  className={cn(
                    'auth-password-reveal',
                    passwordFieldsRevealed
                      ? 'auth-password-reveal--visible'
                      : 'auth-password-reveal--collapsed',
                  )}
                  data-state={passwordFieldsRevealed ? 'visible' : 'collapsed'}
                >
                  {usesSignupPasswordField ? (
                    <PasswordField
                      key="signup-password"
                      autoCapitalize="none"
                      autoComplete="new-password"
                      autoCorrect="off"
                      error={passwordError || undefined}
                      hideLabel="Hide password"
                      id="new-password"
                      isVisible={passwordVisible}
                      label="Create password"
                      name={passwordInputName}
                      ref={passwordInputRef}
                      onAnimationStart={handleCredentialAutofillSignal}
                      onChange={handlePasswordChange}
                      onInput={() => scheduleCredentialDomSync({ revealOnPassword: true })}
                      onKeyDown={handleEmailFormKeyDown}
                      onVisibleChange={setPasswordVisible}
                      placeholder="Create a password"
                      required={passwordFieldsRevealed}
                      showLabel="Show password"
                      spellCheck={false}
                      value={password}
                    />
                  ) : (
                    <PasswordField
                      key="signin-password"
                      autoCapitalize="none"
                      autoComplete="current-password"
                      autoCorrect="off"
                      error={passwordError || undefined}
                      hideLabel="Hide password"
                      id="current-password"
                      isVisible={passwordVisible}
                      label="Password"
                      name={passwordInputName}
                      ref={passwordInputRef}
                      onAnimationStart={handleCredentialAutofillSignal}
                      onChange={handlePasswordChange}
                      onInput={() => scheduleCredentialDomSync({ revealOnPassword: true })}
                      onKeyDown={handleEmailFormKeyDown}
                      onVisibleChange={setPasswordVisible}
                      placeholder="Enter your password"
                      required={passwordFieldsRevealed}
                      showLabel="Show password"
                      spellCheck={false}
                      tabIndex={passwordFieldsRevealed ? undefined : -1}
                      toggleTabIndex={passwordFieldsRevealed ? undefined : -1}
                      value={password}
                    />
                  )}

                  {showSignupControls && !passwordError && (
                    <p
                      aria-live="polite"
                      className={cn(
                        'auth-password-strength',
                        `auth-password-strength--${signupPasswordStrength.tone}`,
                      )}
                    >
                      {signupPasswordStrength.message}
                    </p>
                  )}

                  {showSignupControls && (
                    <PasswordField
                      autoCapitalize="none"
                      autoComplete="new-password"
                      autoCorrect="off"
                      error={confirmPasswordError || undefined}
                      hideLabel="Hide repeated password"
                      id="repeat-new-password"
                      isVisible={confirmPasswordVisible}
                      label="Repeat password"
                      name={confirmPasswordInputName}
                      ref={confirmPasswordInputRef}
                      onAnimationStart={handleCredentialAutofillSignal}
                      onChange={(event) => {
                        setConfirmPassword(event.target.value);
                        if (confirmPasswordError) {
                          setConfirmPasswordError(null);
                        }
                      }}
                      onInput={() => scheduleCredentialDomSync({ revealOnPassword: true })}
                      onKeyDown={handleEmailFormKeyDown}
                      onVisibleChange={setConfirmPasswordVisible}
                      placeholder="Repeat your password"
                      required
                      showLabel="Show repeated password"
                      spellCheck={false}
                      value={confirmPassword}
                    />
                  )}
                </div>
              </div>

              <div
                className={cn(
                  'auth-email-form__actions',
                  passwordFieldsRevealed && 'auth-email-form__actions--revealed',
                )}
              >
                {authStatus && (
                  <div
                    className={cn('auth-form-status', `auth-form-status--${authStatus.tone}`)}
                    role={authStatus.role}
                  >
                    <span>{authStatus.message}</span>
                  </div>
                )}
                <Button
                  className="auth-provider-button"
                  disabled={
                    isLoading ||
                    selectedProvider !== null ||
                    emailLookupSubmitting ||
                    verificationResendSubmitting
                  }
                  fullWidth
                  loading={emailSubmitLoading}
                  onClick={() => {
                    userInitiatedEmailSubmitRef.current = true;
                  }}
                  size="lg"
                  type="submit"
                  variant="primary"
                >
                  {emailLookupSubmitting
                    ? 'Checking email…'
                    : showSignupControls
                      ? 'Create account'
                      : 'Continue with Email'}
                </Button>

                {passwordFieldsRevealed && (
                  <div className="auth-secondary-actions">
                    {emailVerificationPending && (
                      <Button
                        className="auth-secondary-action-button"
                        disabled={selectedProvider !== null || verificationResendSubmitting}
                        loading={verificationResendSubmitting}
                        onClick={handleVerificationResend}
                        type="button"
                        variant="link"
                      >
                        Resend verification email
                      </Button>
                    )}
                    {showPasswordResetAction && (
                      <Button
                        className="auth-secondary-action-button"
                        disabled={selectedProvider !== null || passwordResetSubmitting}
                        loading={passwordResetSubmitting}
                        onClick={handlePasswordReset}
                        type="button"
                        variant="link"
                      >
                        Reset password
                      </Button>
                    )}
                    <Button
                      className="auth-secondary-action-button"
                      disabled={
                        selectedProvider !== null ||
                        emailLookupSubmitting ||
                        passwordResetSubmitting ||
                        verificationResendSubmitting
                      }
                      onClick={handleShowSignInOptions}
                      type="button"
                      variant="link"
                    >
                      Other sign-in options
                    </Button>
                  </div>
                )}
              </div>
            </form>
          )}
        </div>

        <p className="auth-fine-print">
          By continuing, you agree to the BotSpot{' '}
          <Link className="auth-fine-print__link" to="/terms">
            terms
          </Link>{' '}
          and{' '}
          <Link className="auth-fine-print__link" to="/privacy">
            privacy policy
          </Link>
          .
        </p>
      </Panel>
    </div>
  );
};

export default LoginRedirect;
